import { useEffect, useState, lazy } from "react";
import { useAuth } from "react-oidc-context";
import { Routes, Route, useMatch, Navigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import "./config/content/i18n";
import { StandardLayout, ScrollTopOnPageChange } from "./components/layout";
import userApi from "./api/user/userApi";
import {
  AuthCallback,
  SsoAuthCallback,
  AuthErrorPage,
  AuthLogout,
} from "./routes/auth";
import FullScreenLoaderWithMessage from "./components/loaders/FullScreenLoaderWithMessage";
import {
  YourDashboard,
  ManagerDashboard,
  OgAppRedirect,
  ErrorPage,
} from "./routes";
import AppRoutes from "./routes/AppRoutes";
import UserContext, {
  UserContextInterface,
  userContextDefaultValue,
} from "./state/UserContext";
import AppContext from "./state/AppContext";
import OverrideNavItem from "./types/nav/OverrideNavItem";
import SignedInUserDto, {
  SignedInUserUpdatedDetailsDto,
} from "./types/dtos/users/SignedInUserDto";
import {
  advancedTaskHelper,
  colourHelper,
  dateHelper,
  i18nHelper,
  impersonationHelper,
  localStorageHelper,
} from "./helpers";
import inAppSupportHelper from "./helpers/inAppSupportHelper";
import { permissionHelper } from "./helpers/permissionHelper";
import { OfflineWarning } from "./components/common";
import MaintenancePage from "./components/layout/MaintenancePage";
import TestErrorLogger from "./logging/TestErrorLogger";
// Instantiate the singleton logger
import ErrorLogger from "./logging/ErrorLogger";
// Enable behaviour logging to Application Insights
import UserBehaviourLogger from "./logging/UserBehaviourLogger";
import UserwalkthroughType from "./types/generic/UserWalkthroughType";
import YourJourneyRoot from "./routes/your-journey/YourJourneyRoot";
import YourPeopleRoot from "./routes/your-people/YourPeopleRoot";
import AccessibePlugin from "./components/third-party/AccessibePlugin";
import { DemoManagerReset } from "./routes/demo/DemoManagerReset";
import AdminDashboard from "./routes/admin/AdminDashboard";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import CollabDocEditor from "./routes/CollabDocEditor";
import CollabDocViewer from "./routes/CollabDocViewer";

/** Code splitting pages not used by every user */
const ComponentsDemo =
  process.env.NODE_ENV === "development"
    ? lazy(() => import("./routes/ComponentsDemo"))
    : () => null;
const AdminRoot = lazy(() => import("./routes/admin/AdminRoot"));
const AnalyticsModule = lazy(
  () => import("./routes/analytics/AnalyticsModule")
);

impersonationHelper.captureSessionIdFromUrl();

function App() {
  ErrorLogger.init();
  UserBehaviourLogger.init();

  // Constants
  const auth = useAuth();
  const { t } = useTranslation();
  const apiUsers = new userApi(auth.user?.access_token);

  // Authentication
  const isSsoCallback = useMatch({
    path: AppRoutes.auth.ssoCallback,
    end: false,
  });

  const isLogoutRequest = useMatch({
    path: AppRoutes.auth.logout,
    end: false,
  });

  const isRemoteLogoutRequest = useMatch({
    path: AppRoutes.auth.remoteLogoutCallback,
    end: false,
  });

  const [user, setUser] = useState<SignedInUserDto>(
    userContextDefaultValue.user
  );
  const [userHasLoaded, setUserHasLoaded] = useState<boolean>(false);

  const setUserContext = (newUserDetails: SignedInUserDto) => {
    // Convert the dateLoaded string to a Date object
    newUserDetails.dateLoaded = dateHelper.convertDateFromString(
      newUserDetails.dateLoaded
    )!;
    setUser(newUserDetails);
  };

  /** Update the properties on a user which are allowed to be refreshed */
  const setUpdatedUserDetails = (updates: SignedInUserUpdatedDetailsDto) => {
    const updatedUser: SignedInUserDto = {
      ...user,
      notifications: updates.notifications,
      highlightedTasks: updates.highlightedTasks,
      managerUpdateCount: updates.managerUpdateCount,
      myDashboardMode: updates.myDashboardMode,
      profilePic: updates.profilePic,
      dateLoaded: updates.dateLoaded,
      walkthroughSeen: updates.walkthroughSeen,
      impersonation: user.impersonation,
    };
    setUserContext(updatedUser);
  };

  /**
   * Keep the context in step with the API write, so a walkthrough dismissed on one
   * page cannot auto-open on another before the next full user refresh.
   */
  const onWalkthroughSeen = (walkthroughType: UserwalkthroughType) => {
    setUser((currentUser) => {
      const seen = currentUser.walkthroughSeen ?? [];
      if (seen.includes(walkthroughType)) return currentUser;
      return { ...currentUser, walkthroughSeen: [...seen, walkthroughType] };
    });
  };

  const onAdvancedTaskViewed = (
    ownerUserId: number,
    taskTypeId: string,
    userTaskId: string
  ) => {
    const updatedUser: SignedInUserDto = {
      ...user,
      highlightedTasks: advancedTaskHelper.removeTaskHighlightForUserContext(
        user.id,
        ownerUserId,
        taskTypeId,
        userTaskId,
        user.highlightedTasks
      ),
    };
    setUserContext(updatedUser);
  };

  useEffect(() => {
    // When the user details change, update the user deails for error logging
    const logUser =
      user?.id > 0
        ? {
            clientId: user.client.id,
            clientName: user.client.name,
            id: user.id,
            fullName: user.fullName,
          }
        : null;
    ErrorLogger.setActiveUser(logUser);
  }, [user.id]);

  // Set the UserContext
  const userContextProvider: UserContextInterface = {
    user,
    setUser: setUserContext,
    setUpdatedUserDetails,
    markWalkthroughSeen: onWalkthroughSeen,
    removeTaskHighlight: onAdvancedTaskViewed,
  };

  // Page title is set via Context due to the structure of the components
  // and child components needing to update the title in TopBar.tsx
  const [pageTitle, setPageTitle] = useState<string>("");
  const [pageIcon, setPageIcon] = useState<IconDefinition | undefined>(
    undefined
  );
  const [showPageTitleAccent, setShowPageTitleAccent] = useState<boolean>(true);
  const [showTopBarTitleSpacing, setShowTopBarTitleSpacing] =
    useState<boolean>(true);
  const [overrideNavItem, setOverrideNavItem] =
    useState<OverrideNavItem | null>(null);
  const [
    activeDashboardTypeForWalkthrough,
    setActiveDashboardTypeForWalkthrough,
  ] = useState<UserwalkthroughType | null>(null);
  const [sideBarIsOpen, setSideBarIsOpen] = useState<boolean>(false);
  const [accessibilityToolHidden, setAccessibilityToolHidden] =
    useState<boolean>(false);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      loadLoggedInUserDetails();
    }
  }, [auth.isAuthenticated]);

  // Loaders
  const loadLoggedInUserDetails = () => {
    if (!auth.isAuthenticated) return;

    // On successful login, call the API to retrieve the logged in user's details
    auth.clearStaleState();

    apiUsers.getLoggedInUserDetails(
      (apiUserDto: SignedInUserDto) => {
        setUserHasLoaded(true);
        userContextProvider.setUser(apiUserDto);

        // Set the correct file to load translations from
        i18nHelper.setCurrentLanguage(
          apiUserDto.client.id,
          apiUserDto.i18n.cultureCode
        );

        // Set the local storage value for the client MVC route,
        // so we can redirect there if the user gets logged out
        localStorageHelper.setItem(
          "USER_MVC_APP_URL",
          apiUserDto.client.mvcAppUrl
        );

        // Ensure Application Insights user context is set with full details
        UserBehaviourLogger.setActiveUser({
          id: apiUserDto.id,
          clientName: apiUserDto.client.name,
        });

        void inAppSupportHelper.initialise(apiUserDto);
      },
      (error) => {
        console.error("User api error", error);
        ErrorLogger.log("API error loading user details: " + error);
      }
    );
  };

  // Auth events
  switch (auth.activeNavigator) {
    case "signinSilent":
      return (
        <FullScreenLoaderWithMessage
          message={t("Common.Auth.SignInSilentMessage")}
        />
      );
    case "signoutRedirect":
      return (
        <FullScreenLoaderWithMessage
          message={t("Common.Auth.SignOutRedirectMessage")}
        />
      );
  }

  if (auth.isLoading) {
    return (
      <FullScreenLoaderWithMessage
        message={t("Common.Auth.SignInSilentMessage")}
      />
    );
  }

  if (auth.error) {
    return <AuthErrorPage errorMessage={auth.error.message} />;
  }

  const hasUrlHashParam =
    window.location.hash && window.location.hash.indexOf("#code=") === 0;
  if (
    (!auth.isAuthenticated || (auth.isAuthenticated && hasUrlHashParam)) &&
    !isSsoCallback &&
    !isLogoutRequest &&
    !isRemoteLogoutRequest
  ) {
    // If redirected with a code hash, as part of the `returnUrl` redirect process,
    // redirect to the ssoCallbackUrl, but pass the `returnUrl` as a querystring param
    // so we can redirect there when the sign-in process has completed
    let returnUrl = window.location.pathname + window.location.search;
    returnUrl = encodeURIComponent(returnUrl);
    let ssoCallbackUrl = `${AppRoutes.auth.ssoCallback}?returnUrl=${returnUrl}`;

    if (hasUrlHashParam) {
      ssoCallbackUrl = ssoCallbackUrl + window.location.hash;
    }

    window.location.href = ssoCallbackUrl;
    return null;
  }

  // Show the loader until the user details have been loaded,
  // to prevent colour theme flicker
  const isMissingUserDetails = userHasLoaded && user.id === 0;
  if (
    (!userHasLoaded || isMissingUserDetails) &&
    !window.location.pathname.toLowerCase().startsWith("/auth")
  ) {
    if (isMissingUserDetails) {
      loadLoggedInUserDetails();
    }

    return (
      <FullScreenLoaderWithMessage
        message={t("Common.Auth.SignInSilentMessage")}
      />
    );
  }

  const isInMaintenanceMode =
    user.client.maintenanceMode &&
    !permissionHelper.userHasPermission(
      userContextProvider,
      "PLATFORM-CONFIGURATION"
    );

  if (isInMaintenanceMode) {
    return (
      <AppInsightsContext.Provider value={UserBehaviourLogger.reactPlugin}>
        <HelmetProvider>
          <UserContext.Provider value={userContextProvider}>
            <MaintenancePage />
          </UserContext.Provider>
        </HelmetProvider>
      </AppInsightsContext.Provider>
    );
  }

  // Convert the hex colour code to (say) `134, 34, 122` for use in CSS with alpha/opacity
  const accentColourAsRgb = colourHelper.convertHexToRgbNumbersString(
    userContextProvider.user.client.accentHexColour
  );

  return (
    <AppInsightsContext.Provider value={UserBehaviourLogger.reactPlugin}>
      <HelmetProvider>
        <UserContext.Provider value={userContextProvider}>
          <OfflineWarning user={userContextProvider.user} />
          <AppContext.Provider
            value={{
              pageTitle,
              setPageTitle,
              pageIcon,
              setPageIcon,
              showPageTitleAccent,
              setShowPageTitleAccent,
              showTopBarTitleSpacing,
              setShowTopBarTitleSpacing,
              overrideNavItem,
              setOverrideNavItem,
              activeDashboardTypeForWalkthrough,
              setActiveDashboardTypeForWalkthrough,
              sideBarIsOpen,
              setSideBarIsOpen,
              accessibilityToolHidden,
              setAccessibilityToolHidden,
            }}
          >
            <>
              {/* Set the client accent colour via a CSS variable in the head tag */}
              <Helmet>
                <title>
                  {userContextProvider.user.client.productName}
                  {pageTitle ? " - " + pageTitle : ""}
                </title>
                <style type="text/css">
                  {`:root {
                  --color-accent: #${
                    userContextProvider.user.client.accentHexColour
                  };
                  --color-accent-rgb: ${accentColourAsRgb};
                  --color-primary: #${
                    userContextProvider.user.client.primaryHexColour
                  };
                  --color-primary-with-opacity: #${
                    userContextProvider.user.client.primaryHexColour
                  }10;
                  --color-primary-pale: ${colourHelper.createPaleVersion(
                    userContextProvider.user.client.primaryHexColour
                  )};
                  --color-primary-pale-with-opacity: ${colourHelper.createPaleVersionWithOpacity(
                    userContextProvider.user.client.primaryHexColour
                  )};
                  --color-primary-pale-visible: ${colourHelper.createVisiblePaleVersion(
                    userContextProvider.user.client.primaryHexColour
                  )};
                  --color-primary-pale-with-opacity-visible: ${colourHelper.createVisiblePaleVersionWithOpacity(
                    userContextProvider.user.client.primaryHexColour
                  )};
                  --color-background-tint: ${
                    userContextProvider.user.client.backgroundTintHexColour
                      ? `#${userContextProvider.user.client.backgroundTintHexColour}`
                      : colourHelper.createPaleVersionWithOpacity(
                          userContextProvider.user.client.primaryHexColour
                        )
                  };
                  --nav-selected-label-colour: ${colourHelper.navSelectedLabelColour(
                    userContextProvider.user.client.primaryHexColour
                  )};
                }`}
                </style>
              </Helmet>
              <ScrollTopOnPageChange>
                <>
                  <Routes>
                    <Route path="/" element={<StandardLayout />}>
                      <Route
                        index
                        element={
                          <Navigate to={AppRoutes.yourJourney.root} replace />
                        }
                      />
                      <Route
                        path={AppRoutes.yourJourney.root}
                        element={<YourDashboard />}
                      />
                      <Route
                        path={AppRoutes.yourJourney.root + "/*"}
                        element={<YourJourneyRoot />}
                      />
                      <Route
                        path={AppRoutes.yourPeople.root}
                        element={<ManagerDashboard />}
                      />
                      <Route
                        path={AppRoutes.yourPeople.root + "/*"}
                        element={<YourPeopleRoot />}
                      />
                      <Route
                        path={AppRoutes.companyDashboard}
                        element={
                          <OgAppRedirect redirectTo="COMPANY_DASHBOARD" />
                        }
                      />
                      <Route
                        path={AppRoutes.collaborativeDocument.standardPath}
                      >
                        <Route
                          path=":singleFormId"
                          element={<CollabDocEditor key="form-specific-view" />}
                        />
                        <Route
                          path=""
                          element={<CollabDocEditor key="general-doc" />}
                        />
                      </Route>
                      <Route
                        path={AppRoutes.collaborativeDocument.viewCollabDoc}
                        element={<CollabDocViewer />}
                      />
                      <Route
                        path={AppRoutes.companyDashboard}
                        element={
                          <OgAppRedirect redirectTo="COMPANY_DASHBOARD" />
                        }
                      />
                      <Route
                        path={AppRoutes.help}
                        element={<OgAppRedirect redirectTo="HELP" />}
                      />
                      <Route
                        path={AppRoutes.profile}
                        element={<OgAppRedirect redirectTo="PROFILE" />}
                      />
                      <Route
                        path={AppRoutes.changePassword}
                        element={<OgAppRedirect redirectTo="CHANGE_PASSWORD" />}
                      />
                      <Route
                        path={AppRoutes.yourGoals}
                        element={<OgAppRedirect redirectTo="YOUR_GOALS" />}
                      />
                      <Route
                        path={AppRoutes.notificationPreferences}
                        element={
                          <OgAppRedirect redirectTo="NOTIFICATION_PREFERENCES" />
                        }
                      />
                      <Route
                        path={AppRoutes.reviewHistory.path}
                        element={<OgAppRedirect redirectTo="REVIEW_HISTORY" />}
                      />
                      <Route
                        path={AppRoutes.legacyTaskList.path}
                        element={<OgAppRedirect redirectTo="TASK_LIST" />}
                      />
                      <Route
                        path={AppRoutes.analytics.classic}
                        element={<OgAppRedirect redirectTo="ANALYTICS" />}
                      />
                      <Route
                        path={AppRoutes.logout}
                        element={<OgAppRedirect redirectTo="LOGOUT" />}
                      />
                      {process.env.NODE_ENV === "development" && (
                        <Route
                          path={AppRoutes.componentsDemo}
                          element={<ComponentsDemo />}
                        />
                      )}
                      <Route
                        path={AppRoutes.demo.managerReset}
                        element={<DemoManagerReset />}
                      />
                      <Route
                        path={AppRoutes.admin.root}
                        element={<AdminDashboard />}
                      />
                      <Route
                        path={AppRoutes.admin.root + "/*"}
                        element={<AdminRoot />}
                      />
                      <Route
                        path={AppRoutes.admin.oldSystemUserManagement}
                        element={<OgAppRedirect redirectTo="ADMIN" />}
                      />
                      <Route
                        path={AppRoutes.admin.classicUserDetailAdmin.path}
                        element={
                          <OgAppRedirect redirectTo="ADMIN-EMPLOYEE-DETAIL" />
                        }
                      />
                      <Route
                        path={AppRoutes.analytics.root + "/*"}
                        element={<AnalyticsModule />}
                      />
                      <Route
                        path="error-log-test"
                        element={<TestErrorLogger />}
                      />
                      <Route path="auth">
                        <Route path="callback" element={<AuthCallback />} />
                        <Route
                          path="sso-callback"
                          element={<SsoAuthCallback />}
                        />
                        <Route path="logout" element={<AuthLogout />} />
                      </Route>

                      <Route
                        path="*"
                        element={
                          <ErrorPage
                            errorTitle={t("Errors.404.Title")}
                            shortDescription={t("Errors.404.Body")}
                          />
                        }
                      />
                    </Route>
                  </Routes>
                </>
              </ScrollTopOnPageChange>
              <AccessibePlugin />
            </>
          </AppContext.Provider>
        </UserContext.Provider>
      </HelmetProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
